import React, {useContext, useEffect} from "react"
import {ChipStyles, ChipsProps, IChip} from "./models"
import {
    ChipContainer,
    StyledChip,
    StyledConversationalHeading,
    StyledConversationalImg,
    StyledConversationalHeadingContainer,
} from "./components"
import {
    SearchBannerContext,
    replaceSiteUrl,
    useUniqueKey,
    getSearchBannerComponentTestId,
    AI_ASSISTANT_ICON_ALT,
} from "../utils"
import env from "../../../config/env"
import TrackSeoPillsImpression from "../../../events/trackEvent/events/trackSeoPillsImpression"
import TrackSeoPillsClick from "../../../events/trackEvent/events/trackSeoPillsClick"

const handleSeoPillsImpression = (chips: IChip[]) => {
    if (chips?.length > 0) {
        const pillsImpression = chips.reduce((agg, chip) => {
            return agg ? `${agg} | ${chip.text || ""}` : chip.text
        }, "")
        TrackSeoPillsImpression(pillsImpression)
    }
}
export type AdditionalChipProps = {
    isStructuredBrowsingChip?: boolean
}
export const Chips = ({
    overrideStyles,
    content,
    isStructuredBrowsingChip = false,
    isIntelRecs,
}: ChipsProps & AdditionalChipProps) => {
    const {components, isConversational, conversationalHeading} = content
    const chipKeys = useUniqueKey(components?.length ?? 0)
    const alignment = overrideStyles?.alignment
    const chipStyles = {
        backgroundColor: overrideStyles?.backgroundColor,
        border: overrideStyles?.border,
        borderBottom: overrideStyles?.borderBottom,
        borderColor: overrideStyles?.borderColor,
        borderRadius: overrideStyles?.borderRadius,
        textColor: overrideStyles?.textColor,
        fontSize: overrideStyles?.fontSize,
        fontWeight: overrideStyles?.fontWeight,
        letterSpacing: overrideStyles?.letterSpacing,
        textDecoration: overrideStyles?.textDecoration,
        textTransform: overrideStyles?.textTransform,
        hoverTextDecoration: overrideStyles?.hoverTextDecoration,
        hoverBackgroundColor: overrideStyles?.hoverBackgroundColor,
        hoverBorderColor: overrideStyles?.hoverBorderColor,
    }
    const chipsCount = components?.length || 0
    if (chipsCount < 1) {
        console.warn(`${chipsCount} chips are being passed to <Chips />.`)
    }
    useEffect(() => {
        handleSeoPillsImpression(components as IChip[])
    }, [])
    return (
        <>
            {isConversational && conversationalHeading && (
                <StyledConversationalHeadingContainer>
                    <StyledConversationalImg
                        src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/NbyNext.gif`}
                        alt={AI_ASSISTANT_ICON_ALT}
                    />
                    <StyledConversationalHeading>{conversationalHeading}</StyledConversationalHeading>
                </StyledConversationalHeadingContainer>
            )}
            <ChipContainer
                $alignment={alignment}
                $isIntelRecs={isIntelRecs}
                as="div"
                data-testid={getSearchBannerComponentTestId(isIntelRecs, "chips")}
                $isStructuredBrowsingChip={isStructuredBrowsingChip}
            >
                {components?.map((chip, idx) => {
                    return (
                        <Chip
                            key={chipKeys[idx]}
                            text={chip.text}
                            url={chip.url}
                            accessibilityText={chip.accessibilityText}
                            openInNewWindow={chip.openInNewWindow}
                            chipStyles={chipStyles}
                            isStructuredBrowsingChip={isStructuredBrowsingChip}
                            isConversational={isConversational}
                        />
                    )
                })}
            </ChipContainer>
        </>
    )
}

interface ChipProps extends IChip {
    chipStyles: ChipStyles
    isStructuredBrowsingChip: boolean
    isConversational?: boolean
}

const Chip = ({
    text,
    url,
    accessibilityText,
    openInNewWindow,
    chipStyles,
    isStructuredBrowsingChip,
    isConversational,
}: ChipProps) => {
    const {siteUrl} = useContext(SearchBannerContext)
    const formattedUrl = replaceSiteUrl(url, siteUrl)
    const handleClick = () => {
        TrackSeoPillsClick(text, formattedUrl)
    }
    return (
        <StyledChip
            $chipStyles={chipStyles}
            title={accessibilityText}
            href={formattedUrl}
            target={openInNewWindow ? "_blank" : "_self"}
            onClick={handleClick}
            data-testid="search-banner-chip"
            $isStructuredBrowsingChip={isStructuredBrowsingChip}
            $isConversational={isConversational}
        >
            {text}
        </StyledChip>
    )
}

export default Chips
